<template>
    <i
        class="basic-icon"
        data-testid="basic-icon"
        :aria-hidden="!hasAnyListener"
        :class="classes"
    >
        <Suspense>
            <component :is="icon" />
        </Suspense>
    </i>
</template>

<script setup lang="ts">
import { sizesPx, type Size } from '@atoms/BasicIcon/sizes';
import { useListeners } from '@composables/useListeners';

// Due to drawback with dynamic classes of Tailwind's JIT icon component do not have
// color prop. Class in parent component should be used instead, eg. fill-primary.
export interface BasicIconProps {
    name: string;
    size?: Size;
    inheritFill?: boolean;
}

const props = withDefaults(defineProps<BasicIconProps>(), {
    size: '20',
    inheritFill: true,
});

const icon = computed(() => {
    // Not sure why but it is needed to use `props.name` above defineAsyncComponent.
    // Otherwise changing prop value did not fire computed.
    const name = props.name;
    if (!name) {
        console.warn('BasicIcon component\'s "name" property is missing');
        return;
    }
    // Relative path is required due to dynamic imports limitations
    // https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
    return defineAsyncComponent(async () => {
        try {
            return await import(`../../../assets/icons/${name}.svg`);
        } catch (error: unknown) {
            console.warn(
                `[BasicIcon] Icon named "${name}" was not found in /assets/icons directory. \n`,
                error
            );
            return error;
        }
    });
});

const { hasAnyListener } = useListeners();

const classes = computed(() => {
    return {
        'cursor-pointer': hasAnyListener.value,
        '[&_*]:fill-inherit': props.inheritFill,
    };
});

const computedSize = computed(() => {
    return `${sizesPx[props.size]}px`;
});
</script>

<style>
.basic-icon svg {
    width: v-bind(computedSize);
    height: v-bind(computedSize);
}
</style>
